import { useState } from "react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MessageSquare, CheckCircle2 } from "lucide-react";

const empty = { name: "", email: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <div className="mx-auto max-w-7xl px-6 py-16 md:py-24 lg:px-8" data-testid="contact-page">
        <div className="grid gap-12 md:grid-cols-2">
          <div>
            <p className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              Contact
            </p>
            <h1 className="mt-3 font-heading text-4xl font-bold tracking-tighter text-foreground sm:text-5xl">
              Questions? We're here.
            </h1>
            <p className="mt-6 font-body leading-relaxed text-muted-foreground">
              Want to update your preferences, opt out, or ask about how Step Zone
              works? Send us a message and we'll get back to you.
            </p>
            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <MessageSquare className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-heading font-semibold text-foreground">Real replies</p>
                  <p className="font-body text-sm text-muted-foreground">We read every message, usually within a couple of days.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-heading font-semibold text-foreground">Kept private</p>
                  <p className="font-body text-sm text-muted-foreground">Your details are stored securely and never sold.</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            {done ? (
              <div className="rounded-2xl border border-border bg-card p-10 text-center shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="contact-success">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h2 className="mt-6 font-heading text-2xl font-semibold text-foreground">Message sent</h2>
                <p className="mt-3 font-body text-muted-foreground">Thanks for reaching out. We'll be in touch soon.</p>
                <Button variant="outline" className="mt-6 rounded-xl border-2 font-heading font-semibold" onClick={() => { setForm(empty); setDone(false); }} data-testid="contact-reset-button">
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)] md:p-8" data-testid="contact-form">
                <div className="space-y-5">
                  <div>
                    <Label htmlFor="c-name" className="mb-2 block font-body text-sm font-semibold text-foreground">Name</Label>
                    <Input id="c-name" data-testid="contact-input-name" required value={form.name} onChange={set("name")} placeholder="Your name" className="h-12 rounded-xl border-2 border-input" />
                  </div>
                  <div>
                    <Label htmlFor="c-email" className="mb-2 block font-body text-sm font-semibold text-foreground">Email</Label>
                    <Input id="c-email" data-testid="contact-input-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="h-12 rounded-xl border-2 border-input" />
                  </div>
                  <div>
                    <Label htmlFor="c-phone" className="mb-2 block font-body text-sm font-semibold text-foreground">Phone (optional)</Label>
                    <Input id="c-phone" data-testid="contact-input-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" className="h-12 rounded-xl border-2 border-input" />
                  </div>
                  <div>
                    <Label htmlFor="c-message" className="mb-2 block font-body text-sm font-semibold text-foreground">Message</Label>
                    <Textarea id="c-message" data-testid="contact-input-message" required rows={5} value={form.message} onChange={set("message")} placeholder="How can we help?" className="rounded-xl border-2 border-input" />
                  </div>
                  <Button type="submit" data-testid="contact-submit-button" disabled={submitting} className="w-full rounded-xl bg-primary py-6 font-heading text-base font-semibold text-primary-foreground shadow-[0_4px_14px_0_rgba(0,85,255,0.39)] transition-transform hover:-translate-y-0.5 hover:bg-primary/90">
                    {submitting ? "Sending…" : "Send Message"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
